import React, { PropsWithChildren, ReactNode, useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';

import { DefaultTheme, LinkingOptions, NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import { ds } from '@/components/lib/design.system';
import { enableDataDogNavTracking } from '@/data/datadog';
import { AccountEditStack } from '@/feature/account-details/AccountEditStack';
import { AccountStack } from '@/feature/account-details/AccountStack';
import { EditUsernameAndAvatar } from '@/feature/account-details/screens/EditUsernameAndAvatar';
import { DefaultProductSelectScreen } from '@/feature/account-details/screens/GamingLimits/DefaultProductSelectScreen';
import { useModals } from '@/feature/alerts/hooks/use-modals';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsWrapper } from '@/feature/analytics/components/AnalyticsWrapper';
import { GuardBiometricsScreen } from '@/feature/authentication/screens/GuardBiometrics';
import { LoginScreen } from '@/feature/authentication/screens/Login';
import { RegisterScreen } from '@/feature/authentication/screens/Register';
import { BetShareScreen } from '@/feature/bet-share-sbk/screens/BetShareScreen';
import { BetScreen as SbkBetScreen } from '@/feature/bets-sbk/screens/BetScreen';
import { MysteryInfoModal } from '@/feature/betslip-pickem/screens/MysteryInfoModal';
import { CancelEntryScreen } from '@/feature/cancel-entry/screens/CancelEntry';
import { EntryShare } from '@/feature/entry-share/screens/EntryShare';
import { FantasyEventsSheetsScreen } from '@/feature/fantasy-event-sheets/FantasyEventsSheets';
import { SinglePlayerProfileModalScreen } from '@/feature/fantasy-event-sheets/components/SinglePlayerProfileModalScreen';
import { ArcadeGameWebView } from '@/feature/game-details-arcade/screens/ArcadeGameWebView';
import { InviteFriendsStack } from '@/feature/invite-friends/screens/InviteFriendsStack';
import { ReferralHistoryScreen } from '@/feature/invite-friends/screens/ReferralHistoryScreen';
import { ReferralTrackingScreen } from '@/feature/invite-friends/screens/ReferralTrackingScreen';
import { CasinoGameWebViewScreen } from '@/feature/lobby-casino/screens/CasinoGameWebViewScreen';
import { RipsGameWebViewScreen } from '@/feature/lobby-rips/screens/RipsGameWebViewScreen';
import { SbkAllLeaguesScreen } from '@/feature/lobby-sbk/screens/SbkAllLeaguesScreen';
import { PickemAllLeaguesScreen } from '@/feature/lobby/screens/PickemAllLeaguesScreen';
import { MaintenanceScreen } from '@/feature/maintenance/screens/MaintenanceScreen';
import { PlayerProfileSheetsScreen } from '@/feature/player-profile-sheets/PlayerProfileSheets';
import { ProductTransitionOverlay } from '@/feature/product-transition/ProductTransitionOverlay';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { PromoDetails } from '@/feature/promohub/screens/PromoDetails';
import { PushPrimerScreen } from '@/feature/push-primer/screens/PushPrimer';
import { EditQuickAmounts } from '@/feature/quick-amounts/screens/EditQuickAmounts';
import { DepositLimitAdd as DepositLimitAddModal } from '@/feature/responsible-gaming/screens/DepositLimitAdd';
import { DepositLimitEdit as DepositLimitEditModal } from '@/feature/responsible-gaming/screens/DepositLimitEdit';
import { EntryLimitAdd as EntryLimitAddModal } from '@/feature/responsible-gaming/screens/EntryLimitAdd';
import { EntryLimitEdit as EntryLimitEditModal } from '@/feature/responsible-gaming/screens/EntryLimitEdit';
import { MaxSingleEntryLimitAdd as MaxSingleEntryLimitAddModal } from '@/feature/responsible-gaming/screens/MaxSingleEntryLimitAdd';
import { MaxSingleEntryLimitEdit as MaxSingleEntryLimitEditModal } from '@/feature/responsible-gaming/screens/MaxSingleEntryLimitEdit';
import { TimeoutAdd as TimeoutAddModal } from '@/feature/responsible-gaming/screens/TimeoutAdd';
import { GameResultsScreen } from '@/feature/results-arcade/components/GameResultsScreen/GameResultsScreen';
import { SbkEventsSheetsScreen } from '@/feature/sbk-event-sheets/SbkEventSheets';
import { WhatsNewScreen } from '@/feature/whats-new/screens/WhatsNewScreen';
import { useIsGuestAllowedInCurrentJurisdiction } from '@/hooks/use-active-product';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { DepositStack } from '@/navigation/deposit/DepositStack';
import { KycStack, getKycStackOptions } from '@/navigation/kyc/KycStack';
import { FantasyPickSlipStack } from '@/navigation/pickem/fantasyPickSlipStack';
import { BetSlipStack } from '@/navigation/sweeps/betslipStack';
import { WithdrawStack } from '@/navigation/withdraw/WithdrawStack';
import { ComposeProviders } from '@/providers/ComposeProviders';
import { IneligibleCountryScreen } from '@/screens/IneligibleCountryScreen';
import { SuccessModal } from '@/screens/Modals/SuccessModal';
import { logger } from '@/utils/logging';

import { DevDemoStack } from '../feature/devsettings/DevDemoStack';
import { DeveloperScreen } from '../feature/devsettings/screens/DeveloperScreen';
import { EntryScreen } from '../feature/entries-pickem/screens/EntryScreen';
import { isTrustlyAppToAppReturnUrl } from '../feature/kyc/utils/oauth-utils';
import { useResumeEffect } from '../hooks/use-resume';
import { useUserStore } from '../hooks/use-user-store';
import { AllowLocationScreen } from '../screens/AllowLocation/AllowLocation';
import { LogoutScreen } from '../screens/Auth/LogoutScreen';
import { LaunchScreen } from '../screens/Launch/Launch';
import { ErrorModal } from '../screens/Modals/ErrorModal';
import { SplashScreen } from '../screens/Splash/Splash';
import { ModalWebViewScreen } from '../screens/WebView/ModalWebView';
import { ArcadeTabsNavigator } from './arcade/tabs';
import { CasinoTabsNavigator } from './casino/tabs';
import { customGetStateFromPath } from './linking-handlers';
import { navigationRef } from './navigation';
import { PickemTabsNavigator } from './pickem/tabs';
import { PredictionTabsNavigator } from './prediction/tabs';
import { RipsTabsNavigator } from './rips/tabs';
import { screenPresets } from './screen-presets';
import { SweepsTabsNavigator } from './sweeps/tabs';
import { RootStackParamList, ScreenName } from './types';

export const ROOT_NAVIGATOR_ID = 'root_navigator' as const;
const Stack = createNativeStackNavigator<RootStackParamList, typeof ROOT_NAVIGATOR_ID>();

//Routes where if session expires, we should not go back to the Launch screen, as those routes should be accessed
//even if the user is not logged in
const PUBLIC_ROUTES: ScreenName[] = [
    'Launch',
    'Login',
    'Register',
    'AllowLocation',
    'Splash',
    'IneligibleStateModal',
    'IneligibleLocationWeb',
    'ModalWebView',
    'CasinoGameWebView',
];

const GUEST_ROUTES: ScreenName[] = [
    ...PUBLIC_ROUTES,
    'LobbyScreen',
    'FantasyPickSlipStack',
    'FantasyPickSlip',
    'SubmitEntry',
    'FeaturedLineupsScreen',
    'FantasyEventsSheets',
    'PlayerProfileSheets',
    'Feed',
    'FantasySearch',
];

const screenOptions = {
    headerShown: false,
    tabBarBackground: () => <View />,
    freezeOnBlur: true,
};

const AuthGuard: React.FC<PropsWithChildren & { onAuthExpired: () => void }> = ({ children, onAuthExpired }) => {
    // dismissing the modals / sheets here because we're inside the modals provider
    const { dismissAll } = useModals();

    useEffect(() => {
        return useUserStore.subscribe(() => {
            const expired = useUserStore.getState().actions.sessionHasExpired();
            if (expired && !useUserStore.getState().guest) {
                onAuthExpired();
                dismissAll();
            }
        });
    }, [dismissAll, onAuthExpired]);

    const reload = useCallback(() => {
        const expired = useUserStore.getState().actions.sessionHasExpired();
        if (expired && !useUserStore.getState().guest) {
            onAuthExpired();
            dismissAll();
        }
    }, [dismissAll, onAuthExpired]);

    useResumeEffect(reload);

    return <>{children}</>;
};

export const NavigationRouter = ({ providers }: { providers: React.FC<{ children: ReactNode | ReactNode[] }>[] }) => {
    const routeNameRef = useRef<string | undefined>(undefined);
    const handleReady = useCallback(() => {
        routeNameRef.current = navigationRef?.current?.getCurrentRoute()?.name;
        enableDataDogNavTracking(navigationRef);
    }, []);
    const { isProductAvailable, isProductActive, availableProducts, isProductHiddenByUser } = useAvailableProducts();
    const guestAllowed = useIsGuestAllowedInCurrentJurisdiction();

    // nothing on the root stack animates during a product switch, the overlay hides it
    const isProductSwitching = useProductTransitionStore(state => state.isPending);
    const rootScreenOptions = isProductSwitching ? { ...screenOptions, ...screenPresets.noAnimation } : screenOptions;

    const handleStateChange = useCallback(() => {
        const previousRouteName = routeNameRef.current;
        const currentRouteName = navigationRef.current?.getCurrentRoute()?.name;

        // if DEV show navigation screens
        if (__DEV__) {
            // E.g.: INFO  [navigation] EntriesScreen -> EntryScreen
            console.info('[navigation]', `${previousRouteName} -> ${currentRouteName}`);
        }

        if (previousRouteName !== currentRouteName) {
            if (currentRouteName) {
                BetrAnalytics.trackScreenView(currentRouteName);
            }
        }

        if (useUserStore.getState().guest && !GUEST_ROUTES.includes(currentRouteName as ScreenName)) {
            console.info('[navigation]', 'Guest trying to access protected route:', currentRouteName, ' go to login!');
            navigationRef.reset({ routes: [{ name: 'Launch' }] });
        }

        routeNameRef.current = currentRouteName;
    }, []);

    const linking: LinkingOptions<RootStackParamList> = {
        prefixes: ['betrpickem://', 'https://link.betr.app'],
        getStateFromPath: (path, opts) => {
            return customGetStateFromPath(path, opts, {
                isProductActive,
                isProductAvailable,
                isProductHiddenByUser,
                availableProducts,
                guestAllowed,
                routeNameRef,
            });
        },
        async getInitialURL() {
            // return undefined so we don't handle the URL yet (the navigator might not be ready)
            // these links will be handled in the useLinking hook
            return undefined;
        },
        /**
         * Navigation can break on the web if we don't specify all screens in a stack
         * We need to explicitly define each screen that the user can navigate to instead of relying on default screen names
         * Failing to do so can cause navigation to reset to the initial screen
         *
         * While this is sometimes desirable, since we can easily block the user to directly access some screens
         * (such as preventing direct access to verification screens),
         * there are specific cases (like the lobby or account stack) where we need to set an initial route
         * Otherwise, navigation can break when moving forward or backward in the browser,
         * refreshing the screen, or directly accessing certain screens
         *
         * This also affects the app because it might not load correctly all the necessary screens for the app to work properly
         * Some params may not be defined (since are not serialized correctly), or some data is missing,
         * data that normally loads on the screen before the one we are trying to directly access
         *
         * Currently, navigation linking types are broken on nested stacks, so we will use ts-ignore for now
         * (https://github.com/react-navigation/react-navigation/issues/11332 & https://github.com/react-navigation/react-navigation/issues/10876)
         */
        config: {
            initialRouteName: 'Splash',
            screens: {
                Launch: 'auth',
                Login: 'login',
                KycStack: {
                    screens: {
                        TrustlyKycStatusModal: 'trustly-kyc-only-status',
                        KycVerifyingIdpv: 'kyc-verifying-idpv',
                        KycScanId: 'kyc-scan-id',
                    },
                },
                AccountHome: {
                    // see comment above ignoring ts
                    // @ts-ignore
                    initialRouteName: 'Account',
                    path: 'account',
                    screens: {
                        Account: 'account',
                        DepositLimits: 'deposit-limits',
                        EntryLimits: 'entry-limits',
                        MaxSingleEntryLimits: 'max-single-entry-limits',
                        StateLimits: 'state-limits',
                        RealityCheck: 'reality-check',
                        Timeout: 'timeout',
                        HideVerticals: 'hide-verticals',
                        SelfExclusionAdd: 'self-exclusion',
                        TransactionHistory: 'transaction-history',
                        AccountDetails: 'account-details',
                        YearEndStatement: 'year-end-statement',
                        AccountSummary: 'account-summary',
                        PlayHistory: 'play-history/:product',
                        WalletHistory: 'wallet-history/:wallet',
                        StateSelector: 'state-selector',
                        TransactionDetails: 'transaction-details',
                        Preferences: 'preferences',
                        ResponsibleGaming: { path: 'responsible-gaming', alias: ['gaming-limits'] },
                        ManageDebitCards: {
                            path: 'manage-debit-cards',
                            alias: [{ path: 'update-expired-card', exact: true }],
                        },
                    },
                },
                PromoDetails: 'promos/:promoId',
                InviteFriends: {
                    screens: {
                        InviteFriends: 'invite-friends',
                    },
                },
                PickemHome: {
                    path: 'picks',
                    screens: {
                        Lobby: {
                            path: 'home',
                            // see comment above ignoring ts
                            // @ts-ignore
                            initialRouteName: 'LobbyScreen',
                            screens: {
                                LobbyScreen: {
                                    path: ':league',
                                },
                            },
                        },
                    },
                },
                FantasyEventsSheets: 'picks/home/:league/:eventId/:playerId?',
                FantasyPickSlipStack: {
                    screens: {
                        FantasyPickSlip: 'picks/fantasy-pick-slip',
                        SubmitEntry: 'picks/submit-entry',
                    },
                },
                DepositStack: {
                    screens: {
                        DepositScreen: 'deposit',
                        DepositStatusModal: 'deposit-status',
                        CoinPackageScreen: 'purchase/:product?',
                    },
                },
                EntryScreen: 'picks/entry',
                EditQuickAmounts: 'picks/edit-quick-amounts',
                MysteryInfoModal: 'picks/mystery-info',
                CancelEntry: 'picks/cancel-entry',
                EntryShare: 'picks/entry-share',
                ArcadeHome: {
                    path: 'arcade',
                    screens: {
                        ArcadeLobby: {
                            screens: {
                                ArcadeLobbyScreen: 'home',
                            },
                        },
                    },
                },
                CasinoHome: {
                    path: 'casino',
                    screens: {
                        CasinoLobby: {
                            screens: {
                                CasinoLobbyScreen: 'home',
                            },
                        },
                    },
                },
                RipsHome: {
                    path: 'rips',
                    screens: {
                        RipsLobby: {
                            screens: {
                                RipsLobbyScreen: 'home',
                            },
                        },
                    },
                },
                SweepsHome: {
                    path: 'social',
                    screens: {
                        SweepsLobby: {
                            screens: {
                                SweepsLobbyScreen: 'home',
                            },
                        },
                    },
                },
                PredictionHome: {
                    path: 'prediction',
                    screens: {
                        PredictionLobby: {
                            screens: {
                                PredictionLobbyScreen: 'home',
                            },
                        },
                    },
                },
                ModalWebView: {
                    path: 'webview/:uri',
                    parse: {
                        uri: (uri: string) => `https://${decodeURIComponent(uri)}`,
                    },
                    stringify: {
                        uri: (uri: string) => encodeURIComponent(uri.replace(/^https:\/\//, '')),
                    },
                },
            },
        },
        filter(url) {
            if (routeNameRef.current === 'TrustlyWebview' && isTrustlyAppToAppReturnUrl(url)) {
                return false;
            }
            // Prevent deep linking to auth screens if user is logged in
            if (url.includes('auth') || url.includes('login')) {
                return useUserStore.getState().actions.sessionHasExpired();
            }
            return true;
        },
    };

    return (
        <NavigationContainer
            linking={linking}
            ref={navigationRef}
            onReady={handleReady}
            documentTitle={{ enabled: false }}
            onStateChange={handleStateChange}
            theme={{
                ...DefaultTheme,
                colors: { ...DefaultTheme.colors, background: ds.colorValue.background.neutral.base },
            }}
        >
            <ComposeProviders providers={providers}>
                <AnalyticsWrapper navigationRef={navigationRef}>
                    <AuthGuard
                        onAuthExpired={() => {
                            if (!routeNameRef.current) {
                                logger.debug(
                                    '[AUTH GUARD]',
                                    'got auth expired but routes not setup yet. Can probably ignore.'
                                );
                                return;
                            }
                            logger.debug('[AUTH GUARD]', 'expired');
                            if (PUBLIC_ROUTES.includes(routeNameRef.current as ScreenName)) {
                                logger.debug(
                                    '[AUTH GUARD]',
                                    'expired, but user in a public route.',
                                    routeNameRef.current
                                );
                            } else {
                                logger.debug(
                                    '[AUTH GUARD]',
                                    `expired, user in a protected route(${routeNameRef.current}). Reset to Login.`,
                                    routeNameRef.current
                                );
                                const email = useUserStore.getState().profile?.email;
                                navigationRef.reset({
                                    routes: [{ name: 'Login', params: { initialEmail: email } }],
                                });
                            }
                        }}
                    >
                        <View style={styles.flex}>
                            <Stack.Navigator
                                screenOptions={rootScreenOptions}
                                initialRouteName={'Splash'}
                                id={ROOT_NAVIGATOR_ID}
                            >
                                <Stack.Screen
                                    name="Splash"
                                    component={SplashScreen}
                                    options={screenPresets.noAnimation}
                                />
                                <Stack.Screen
                                    name="Launch"
                                    component={LaunchScreen}
                                    options={screenPresets.noAnimation}
                                />
                                <Stack.Screen
                                    name="IneligibleLocationWeb"
                                    component={IneligibleCountryScreen}
                                    options={screenPresets.noAnimation}
                                />
                                <Stack.Screen
                                    name="AllowLocation"
                                    component={AllowLocationScreen}
                                    // enables scroll on web
                                    options={{ contentStyle: { flex: 1 } }}
                                />
                                <Stack.Screen name="Logout" component={LogoutScreen} />
                                {/** Root screens */}
                                <Stack.Screen
                                    name="FantasyEventsSheets"
                                    component={FantasyEventsSheetsScreen}
                                    options={screenPresets.circularTransparentSheet}
                                />
                                <Stack.Screen
                                    name="SbkEventsSheets"
                                    component={SbkEventsSheetsScreen}
                                    options={screenPresets.circularTransparentSheet}
                                />
                                <Stack.Screen
                                    name="PlayerProfileSheets"
                                    component={PlayerProfileSheetsScreen}
                                    options={screenPresets.circularTransparentSheet}
                                />
                                <Stack.Screen
                                    name="PickemAllLeaguesScreen"
                                    component={PickemAllLeaguesScreen}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="SbkAllLeaguesScreen"
                                    component={SbkAllLeaguesScreen}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="PickemHome"
                                    component={PickemTabsNavigator}
                                    options={screenPresets.noAnimation}
                                />
                                <Stack.Screen
                                    name="SweepsHome"
                                    component={SweepsTabsNavigator}
                                    options={screenPresets.noAnimation}
                                />
                                <Stack.Screen
                                    name="ArcadeHome"
                                    component={ArcadeTabsNavigator}
                                    options={screenPresets.noAnimation}
                                />
                                <Stack.Screen
                                    name="CasinoHome"
                                    component={CasinoTabsNavigator}
                                    options={screenPresets.noAnimation}
                                />
                                <Stack.Screen
                                    name="PredictionHome"
                                    component={PredictionTabsNavigator}
                                    options={screenPresets.noAnimation}
                                />
                                <Stack.Screen
                                    name="RipsHome"
                                    component={RipsTabsNavigator}
                                    options={screenPresets.noAnimation}
                                />
                                <Stack.Screen
                                    name="ArcadeGameWebView"
                                    component={ArcadeGameWebView}
                                    options={{ ...screenPresets.fullScreenCover, ...screenPresets.noAnimation }}
                                />
                                <Stack.Screen
                                    name="ArcadeGameResults"
                                    component={GameResultsScreen}
                                    options={{ ...screenPresets.fullScreenCover, ...screenPresets.noAnimation }}
                                />
                                <Stack.Screen
                                    name="AccountHome"
                                    component={AccountStack}
                                    options={screenPresets.fullScreenCover}
                                />
                                <Stack.Screen
                                    name="DefaultProductSelect"
                                    component={DefaultProductSelectScreen}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="EditUsernameAndAvatar"
                                    component={EditUsernameAndAvatar}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="PromoDetails"
                                    component={PromoDetails}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen name="ReferralHistory" component={ReferralHistoryScreen} />
                                <Stack.Screen name="ReferralTracking" component={ReferralTrackingScreen} />
                                {/** Pickem entries */}
                                <Stack.Screen
                                    name="EntryScreen"
                                    component={EntryScreen}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="CancelEntry"
                                    component={CancelEntryScreen}
                                    options={screenPresets.fullScreenCover}
                                />
                                <Stack.Screen
                                    name="InviteFriends"
                                    component={InviteFriendsStack}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="EntryShare"
                                    component={EntryShare}
                                    options={screenPresets.fullScreenCover}
                                />
                                <Stack.Screen
                                    name="PushPrimer"
                                    component={PushPrimerScreen}
                                    options={screenPresets.fullScreenCover}
                                />
                                {/** Sbk Bet slip */}
                                <Stack.Screen
                                    name="BetSlipStack"
                                    component={BetSlipStack}
                                    options={screenPresets.sheet()}
                                />
                                {/* Sbk My bets */}
                                <Stack.Screen
                                    name="SbkBetScreen"
                                    component={SbkBetScreen}
                                    options={screenPresets.sheet()}
                                />
                                {/** SBK Bet Share */}
                                <Stack.Screen
                                    name="BetShareScreen"
                                    component={BetShareScreen}
                                    options={screenPresets.fullScreenCover}
                                />
                                {/**Generic */}
                                <Stack.Screen
                                    name="ErrorModal"
                                    component={ErrorModal}
                                    options={screenPresets.fullScreenCover}
                                />
                                <Stack.Screen
                                    name="IneligibleStateModal"
                                    component={ErrorModal}
                                    options={screenPresets.fullScreenCover}
                                />
                                <Stack.Screen
                                    name="ModalWebView"
                                    component={ModalWebViewScreen}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="CasinoGameWebView"
                                    component={CasinoGameWebViewScreen}
                                    options={screenPresets.fullScreenCover}
                                />
                                <Stack.Screen
                                    name="RipsGameWebView"
                                    component={RipsGameWebViewScreen}
                                    options={screenPresets.fullScreenCover}
                                />
                                <Stack.Screen
                                    name="SuccessModal"
                                    component={SuccessModal}
                                    options={screenPresets.fullScreenCover}
                                />
                                <Stack.Screen
                                    name="AccountEditStack"
                                    component={AccountEditStack}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="DepositLimitAdd"
                                    component={DepositLimitAddModal}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="DepositLimitEdit"
                                    component={DepositLimitEditModal}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="MaxSingleEntryLimitEdit"
                                    component={MaxSingleEntryLimitEditModal}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="EntryLimitAdd"
                                    component={EntryLimitAddModal}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="EntryLimitEdit"
                                    component={EntryLimitEditModal}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="MaxSingleEntryLimitAdd"
                                    component={MaxSingleEntryLimitAddModal}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="TimeoutAdd"
                                    component={TimeoutAddModal}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="Login"
                                    component={LoginScreen}
                                    options={screenPresets.transparentFullScreenCover}
                                />
                                <Stack.Screen
                                    name="GuardBiometricsScreen"
                                    component={GuardBiometricsScreen}
                                    options={screenPresets.fullScreenCover}
                                />
                                <Stack.Screen
                                    name="Register"
                                    component={RegisterScreen}
                                    options={screenPresets.transparentFullScreenCover}
                                />
                                {/* pickem pickslip */}
                                <Stack.Screen
                                    name="FantasyPickSlipStack"
                                    component={FantasyPickSlipStack}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="DepositStack"
                                    component={DepositStack}
                                    options={screenPresets.fullScreenCover}
                                />
                                <Stack.Screen
                                    name="WithdrawStack"
                                    component={WithdrawStack}
                                    options={screenPresets.fullScreenCover}
                                />
                                <Stack.Screen name="KycStack" component={KycStack} options={getKycStackOptions} />
                                <Stack.Screen
                                    name="EditQuickAmounts"
                                    component={EditQuickAmounts}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="MysteryInfoModal"
                                    component={MysteryInfoModal}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="SinglePlayerProfileModalScreen"
                                    component={SinglePlayerProfileModalScreen}
                                    options={screenPresets.sheet()}
                                />
                                <Stack.Screen
                                    name="MaintenanceScreen"
                                    component={MaintenanceScreen}
                                    options={screenPresets.gestureDisabled}
                                />
                                <Stack.Screen
                                    name="WhatsNewScreen"
                                    component={WhatsNewScreen}
                                    options={screenPresets.fullScreenCover}
                                />
                                {/* dev settings screens */}
                                <Stack.Screen name="DeveloperScreen" component={DeveloperScreen} />
                                <Stack.Screen name="DevDemoStack" component={DevDemoStack} />
                            </Stack.Navigator>
                            <ProductTransitionOverlay />
                        </View>
                    </AuthGuard>
                </AnalyticsWrapper>
            </ComposeProviders>
        </NavigationContainer>
    );
};

const styles = StyleSheet.create({
    flex: { flex: 1 },
});
